import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { markInstanceAttendance } from "../../lib/schools";
import { buildAttendanceWhatsAppMessage } from "../../lib/schoolFeeMessages";
import {
  downloadRosterCsv,
  filterRosterByQuery,
  formatCheckInTime,
  rosterSummary,
} from "../../lib/classDashboard";
import { ReadableCard, ReadableNavButton } from "../Sticker";
import { FieldHint, FORM_STYLE_CARD_CLASS } from "../createEvent/CreateEventFormUi";
import WhatsAppFollowUpPrompt from "../WhatsAppFollowUpPrompt";
import AttendanceSummaryBar from "./AttendanceSummaryBar";
import AttendanceStudentRow from "./AttendanceStudentRow";
import AddWalkInModal from "./AddWalkInModal";

export default function RosterAttendanceTab({
  instanceId,
  className,
  schoolName,
  sessionLabel,
  rsvps = [],
  loading,
  cancelled,
  onRosterChange,
}) {
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [checkInTimes, setCheckInTimes] = useState({});
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [followUp, setFollowUp] = useState(null);

  const filtered = useMemo(() => filterRosterByQuery(rsvps, search), [rsvps, search]);
  const summary = useMemo(() => rosterSummary(rsvps), [rsvps]);

  async function markPresent(rsvp) {
    if (!instanceId || cancelled) return;
    setBusyId(rsvp.id);
    try {
      const result = await markInstanceAttendance(
        instanceId,
        rsvp.organizer_id || undefined,
        rsvp.id,
      );
      const time = formatCheckInTime();
      setCheckInTimes((prev) => ({ ...prev, [rsvp.id]: time }));
      onRosterChange?.(result?.rsvp || { ...rsvp, status: "ATTENDED" });
      toast.success(`${rsvp.name || "Student"} checked in`);
      setFollowUp({
        phone: rsvp.phone,
        name: rsvp.name,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  }

  function handleExport() {
    downloadRosterCsv(filtered, `class-roster-${instanceId || "session"}.csv`);
    toast.success("CSV downloaded");
  }

  return (
    <div className="max-w-page mx-auto px-4 mt-6 space-y-4" data-testid="class-dash-roster-tab">
      {cancelled ? (
        <p className="font-ui text-[14px] text-ink/80 bg-cream border border-black/[0.08] rounded-card px-4 py-3">
          This session is cancelled. Roster is read-only.
        </p>
      ) : null}

      <ReadableCard className={FORM_STYLE_CARD_CLASS}>
        <AttendanceSummaryBar
          expected={summary.expected}
          checkedIn={summary.checkedIn}
          remaining={summary.remaining}
          search={search}
          onSearchChange={setSearch}
          onExportCsv={handleExport}
        />
      </ReadableCard>

      <ReadableCard className={FORM_STYLE_CARD_CLASS}>
        {loading ? (
          <FieldHint>Loading roster…</FieldHint>
        ) : filtered.length === 0 ? (
          <FieldHint>
            {rsvps.length === 0
              ? "No students booked for this date yet. Add a walk-in below."
              : "No students match your search."}
          </FieldHint>
        ) : (
          <ul className="divide-y-0" data-testid="class-dash-roster-list">
            {filtered.map((rsvp) => (
              <AttendanceStudentRow
                key={rsvp.id}
                rsvp={rsvp}
                checkInTime={checkInTimes[rsvp.id]}
                busy={busyId === rsvp.id}
                onMarkPresent={() => markPresent(rsvp)}
              />
            ))}
          </ul>
        )}
      </ReadableCard>

      {!cancelled && instanceId ? (
        <ReadableNavButton
          type="button"
          variant="primary"
          className="w-full py-3"
          onClick={() => setShowWalkIn(true)}
          data-testid="class-dash-add-walk-in"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          Add Walk-in Student
        </ReadableNavButton>
      ) : null}

      {showWalkIn ? (
        <AddWalkInModal
          instanceId={instanceId}
          className={className}
          schoolName={schoolName}
          sessionLabel={sessionLabel}
          onClose={() => setShowWalkIn(false)}
          onDone={(rsvp) => {
            if (rsvp) onRosterChange?.(rsvp, { append: true });
          }}
        />
      ) : null}

      {followUp ? (
        <WhatsAppFollowUpPrompt
          title="Checked in"
          description={`Optionally confirm with ${followUp.name || "the student"} on WhatsApp.`}
          phone={followUp.phone}
          message={buildAttendanceWhatsAppMessage({
            studentName: followUp.name,
            className,
            schoolName,
          })}
          onClose={() => setFollowUp(null)}
          testId="roster-attendance-whatsapp"
        />
      ) : null}
    </div>
  );
}
